import { useEffect, useState } from "react";
import { useFleet } from "../components/providers/FleetProvider";

/**
 * What the console knows about a detection zone, keyed by the zone id events carry.
 *
 * Events reference only a zone id, but almost everything the log wants to say needs the trigger
 * that owns that zone: the collision/side-pass split comes from the zone NAME convention (see
 * shared/helpers/zoneNameHelpers), and which relays fired comes from the trigger's endpoints. One
 * pass over the fleet's triggers recovers all of it without a per-event lookup.
 */

export type ZoneInfo = {
    name?: string;
    /** Relay endpoints wired to the trigger that watches this zone. */
    relays: string[];
    /** When the trigger was last edited — lets a report say whether config changed after an event. */
    lastModified?: Date;
};

export function useZoneIndex(): Map<string, ZoneInfo> {
    // Org-wide, not group-scoped: lookups are by zone id, so the extra entries cost nothing, and a
    // group with no devices of its own would otherwise lose the names for events it can still show.
    const { allDevices, loadTriggers } = useFleet();
    const [map, setMap] = useState<Map<string, ZoneInfo>>(new Map());

    useEffect(() => {
        if (allDevices.length === 0) return;
        let cancelled = false;
        loadTriggers().then((byFacility) => {
            if (cancelled) return;
            const next = new Map<string, ZoneInfo>();
            byFacility.forEach((triggers) => {
                for (const t of triggers) {
                    const zoneId = t.detectionZone?.id ?? t.detectionZoneId;
                    if (!zoneId) continue;
                    const relays = (t.eventTriggerClientEndpoints ?? [])
                        .map((join) => join.clientEndpoint as { endpointName?: string; $type?: string } | undefined)
                        .filter((ep): ep is { endpointName?: string; $type?: string } => Boolean(ep?.$type?.includes("WebRelay")))
                        .map((ep) => ep.endpointName ?? "Relay");
                    next.set(zoneId, {
                        name: t.detectionZone?.name,
                        relays,
                        lastModified: t.lastModified ? new Date(t.lastModified) : undefined,
                    });
                }
            });
            setMap(next);
        });
        return () => {
            cancelled = true;
        };
    }, [allDevices, loadTriggers]);

    return map;
}

/** Zone id → name, for callers that only need the collision/side-pass split. */
export function useZoneNames(): Map<string, string> {
    const index = useZoneIndex();
    const names = new Map<string, string>();
    index.forEach((info, id) => {
        if (info.name) names.set(id, info.name);
    });
    return names;
}
